import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchbase } from './common.api';
import { call, all, put, takeEvery } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { FileData } from '../../components/chonky';
import { rollbar } from '../../utils/rollbar.util';

export type FolderQueryParams = {
  project_id: number;
  folder_path: string;
  folder_name?: string;
};

export type FilesPutParams = {
  project_id: number;
  src?: string;
  dst?: string;
  cpy?: string;
  srcDir?: string;
  dstDir?: string;
  cpyDir?: string;
};

export type FileQueryParams = {
  project_id: number;
  file_path: string;
};

type FileMutationParams = {
  project_id: number;
  file_path: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file?: File;
};

type FileUploadPayload = {
  url: string;
  fields: {
    'Content-Type': string;
    'Content-Disposition': string;
    'x-amz-meta-creator-id': string;
    'x-amz-meta-file-path': string;
    'x-amz-meta-project-id': string;
    key: string;
    AWSAccessKeyId: string;
    'x-amz-security-token': string;
    policy: string;
    signature: string;
  };
  file: File;
  file_id: number;
};

export type FileInfo = {
  folders: string[];
  files: {
    file: string;
    date: string;
    size: number;
  }[];
  folder_path: string;
};

export interface S3 {
  ResponseMetadata: ResponseMetadata;
  IsTruncated: boolean;
  Contents: Content[];
  CommonPrefixes: CommonPrefix[];
  Name: string;
  Prefix: string;
  Delimiter: string;
  MaxKeys: number;
  EncodingType: string;
  KeyCount: number;
}

export interface ResponseMetadata {
  RequestId: string;
  HostId: string;
  HTTPStatusCode: number;
  HTTPHeaders: Httpheaders;
  RetryAttempts: number;
}

export interface Httpheaders {
  'x-amz-id-2': string;
  'x-amz-request-id': string;
  date: string;
  'x-amz-bucket-region': string;
  'content-type': string;
  'transfer-encoding': string;
  server: string;
}

export interface Content {
  Key: string;
  LastModified: string;
  ETag: string;
  Size: number;
  StorageClass: string;
}

export interface CommonPrefix {
  Prefix: string;
}

export const file = createApi({
  reducerPath: 'file',
  baseQuery: fetchbase,
  tagTypes: ['Files', 'File'],
  endpoints: builder => ({
    getFolder: builder.query<FileInfo, FolderQueryParams>({
      query: ({ project_id, folder_path }: FolderQueryParams) => ({
        url: `file/${project_id}`,
        method: 'GET',
        params: {
          folder_path: folder_path || undefined,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: 'Files', id: `${arg.folder_path}}` },
      ],
    }),
    getFile: builder.query<object, FileQueryParams>({
      query: ({ project_id, file_path }: FileQueryParams) => ({
        url: `file/${project_id}`,
        method: 'GET',
        params: {
          file_path: file_path || undefined,
        },
      }),
      // providesTags: (result, error, arg) => [{ type: 'Files', id: `${arg.file_path}${arg.file_path}` }],
    }),
    createFile: builder.mutation<object, FileMutationParams>({
      query: ({
        project_id,
        file_path,
        file_name,
        file_type,
        file_size,
      }: FileMutationParams) => ({
        url: `file/${project_id}`,
        method: 'POST',
        body: {
          file_path: file_path || undefined,
          file_name: file_name || undefined,
          file_type: file_type || undefined,
          file_size: file_size || undefined,
        },
      }),
    }),
    createFolder: builder.mutation<object, FolderQueryParams>({
      query: ({ project_id, folder_path }: FolderQueryParams) => ({
        url: `file/${project_id}`,
        method: 'POST',
        body: {
          folder_path: folder_path || undefined,
        },
      }),
      invalidatesTags: ['Files'],
    }),
    deleteFolder: builder.mutation<object, FolderQueryParams>({
      query: ({ project_id, folder_path }: FolderQueryParams) => ({
        url: `file/${project_id}`,
        method: 'DELETE',
        body: {
          folder_path: folder_path || undefined,
        },
      }),
      invalidatesTags: ['Files'],
    }),
    deleteFile: builder.mutation<object, FileQueryParams>({
      query: ({ project_id, file_path }: FileQueryParams) => ({
        url: `file/${project_id}`,
        method: 'DELETE',
        body: {
          file_path: file_path || undefined,
        },
      }),
      invalidatesTags: ['Files'],
    }),
    copyFile: builder.mutation<object, FilesPutParams>({
      query: ({
        project_id,
        src,
        dst,
        cpy,
        srcDir,
        dstDir,
        cpyDir,
      }: FilesPutParams) => ({
        url: `file/${project_id}`,
        method: 'PUT',
        body: {
          src: src || undefined,
          dst: dst || undefined,
          cpy: cpy || undefined,
          srcDir: srcDir || undefined,
          dstDir: dstDir || undefined,
          cpyDir: cpyDir || undefined,
        },
      }),
      invalidatesTags: ['Files'],
    }),
  }),
});
type FileAction = {
  requestId: string;
  fileId: string;
  status: string;
};
type FileActions = {
  [key: string]: FileAction;
};

export type FileMap = {
  [key: string]: FileData;
};

type InitialFileState = {
  fileActions: FileActions;
  requestIds: FileActions;
  projects: {
    [key: string]: {
      file: FileMap;
    };
  };
};

export const {
  useCopyFileMutation,
  useCreateFileMutation,
  useCreateFolderMutation,
  useGetFileQuery,
  useDeleteFileMutation,
  useDeleteFolderMutation,
  useGetFolderQuery,
  useLazyGetFileQuery,
  useLazyGetFolderQuery,
  usePrefetch: useFilesPrefetch,
} = file;
/********* */
/* reducer */
/********* */
export const fileBrowserSlice = createSlice({
  name: 'fileBrowser',
  initialState: {
    fileActions: {} as FileActions,
    requestIds: {} as FileActions,
    file: {} as FileMap,
    projects: {} as {
      [key: string]: {
        file: FileMap;
      };
    },
  },
  reducers: {
    fileAction: (state, action: PayloadAction<FileActions>) => {
      state = { ...state, ...action.payload };
    },
    setFilesLoading: (state, action: PayloadAction<FileData[]>) => {
      for (let file of action.payload) {
        state.file[file.id].loading = true;
      }
    },
    clearFileAction: (
      state,
      action: PayloadAction<{ fileId?: string; requestId?: string }>
    ) => {
      if (action.payload.fileId) {
        state.file[action.payload.fileId].loading = false;
        delete state.requestIds[
          state.fileActions[action.payload.fileId].requestId
        ];
        delete state.fileActions[action.payload.fileId];
      } else if (action.payload.requestId) {
        state.file[state.requestIds[action.payload.requestId].fileId].loading =
          false;
        delete state.fileActions[
          state.requestIds[action.payload.requestId].fileId
        ];
        delete state.requestIds[action.payload.requestId];
      }
    },
  },
  extraReducers: builder =>
    builder
      .addMatcher(file.endpoints.getFolder.matchFulfilled, (state, action) => {
        const { files, folder_path } = action.payload;
        let folderMap: any = { '/': { size: 0, modDate: 0 } };
        const currentFileIds: string[] = Object.keys(state.file);
        const newFileIds: string[] = [];
        if (files) {
          for (let file of files) {
            if (file.file.match('.hidden')) {
              //this means it is a folder
              const parentFolderSlice = file.file.split('/').slice(-3)[0];
              // const parentFolderSlice = file.file.split('/').slice(0, -2).join('/')
              const folder_name = file.file
                .replace('/.hidden', '')
                .split('/')
                .slice(-1)[0];
              const parentFolder =
                parentFolderSlice === folder_name ? '/' : parentFolderSlice;
              const filedata: FileData = {
                id: folder_path + file.file.replace('.hidden', ''),
                name: folder_name,
                openable: true,
                isDir: true,
                parentFolder: parentFolder,
              };
              state.file[filedata.id] = { ...filedata };
              newFileIds.push(filedata.id);
            } else {
              // else it is just a file
              const parentFolderSlice = file.file.split('/').slice(-2)[0];
              // const parentFolderSlice = file.file.split('/').slice(0, -1).join('/')
              const file_name = file.file.split('/').slice(-1)[0];
              const parentFolder =
                parentFolderSlice === file_name ? '/' : parentFolderSlice;
              const filedata: FileData = {
                id: folder_path + file.file,
                name: file_name,
                size: file.size,
                modDate: file.date,
                parentFolder: parentFolder,
              };
              state.file[filedata.id] = {
                ...state.file[filedata.id],
                ...filedata,
              };
              newFileIds.push(filedata.id);
              if (Object.keys(folderMap).includes(parentFolder)) {
                folderMap[parentFolder] = {
                  size: filedata.size
                    ? folderMap[parentFolder].size + filedata.size
                    : folderMap[parentFolder].size,
                  modDate:
                    filedata.modDate &&
                    filedata.modDate > folderMap[parentFolder].modDate
                      ? filedata.modDate
                      : folderMap[parentFolder].modDate,
                };
              } else {
                folderMap[parentFolder] = {
                  size: filedata.size,
                  modDate: filedata.modDate,
                };
              }
            }
          }
        }
        //Remove file that are no longer there
        let difference = currentFileIds.filter(x => !newFileIds.includes(x));
        for (let diff of difference) delete state.file[diff];
        // Determine folder sizes and dates by suming contents
        for (let file of Object.values(state.file).reverse()) {
          if (file && file.isDir && file.parentFolder && folderMap[file.name]) {
            if (Object.keys(folderMap).includes(file.parentFolder)) {
              folderMap[file.parentFolder] = {
                size: folderMap[file.name].size
                  ? folderMap[file.parentFolder].size +
                    folderMap[file.name].size
                  : folderMap[file.parentFolder].size,
                modDate:
                  folderMap[file.name].modDate &&
                  folderMap[file.name].modDate >
                    folderMap[file.parentFolder].modDate
                    ? folderMap[file.name].modDate
                    : folderMap[file.parentFolder].modDate,
              };
            } else {
              folderMap[file.parentFolder] = {
                size: file.size ? file.size : folderMap[file.name].size,
                modDate: file.modDate
                  ? file.modDate
                  : folderMap[file.name].modDate,
              };
            }
          }
        }
        for (let file of Object.values(state.file)) {
          if (file.isDir) {
            state.file[file.id] = { ...file, ...folderMap[file.name] };
          }
        }
        // state.file = { ...myfile }
      })
      .addMatcher(file.endpoints.getFile.matchPending, (state, action) => {
        const fileAction = {
          fileId: action.meta.arg.originalArgs.file_path,
          requestId: action.meta.requestId,
          status: 'download',
        };
        state.fileActions[action.meta.arg.originalArgs.file_path] = fileAction;
        state.requestIds[action.meta.requestId] = fileAction;
        state.file[action.meta.arg.originalArgs.file_path].loading = true;
      })
      .addMatcher(file.endpoints.createFile.matchPending, (state, action) => {
        const file_id =
          action.meta.arg.originalArgs.file_path +
          action.meta.arg.originalArgs.file_name;
        const fileAction = {
          fileId: file_id,
          requestId: action.meta.requestId,
          status: 'upload',
        };
        state.fileActions[file_id] = fileAction;
        state.requestIds[action.meta.requestId] = fileAction;
        state.file[file_id] = {
          id: file_id,
          name: action.meta.arg.originalArgs.file_name,
          loading: true,
        };
      })
      .addMatcher(file.endpoints.createFolder.matchPending, (state, action) => {
        const file_id = action.meta.arg.originalArgs.folder_path.replace(
          '.hidden',
          ''
        );
        const fileAction = {
          fileId: file_id,
          requestId: action.meta.requestId,
          status: 'upload',
        };
        state.fileActions[file_id] = fileAction;
        state.requestIds[action.meta.requestId] = fileAction;
        state.file[file_id] = {
          id: file_id,
          isDir: true,
          name: action.meta.arg.originalArgs.folder_name || '',
          loading: true,
        };
      })
      .addMatcher(
        file.endpoints.createFolder.matchFulfilled,
        (state, action) => {
          const file_id = action.meta.arg.originalArgs.folder_path.replace(
            '.hidden',
            ''
          );
          state.file[file_id].loading = false;
          delete state.fileActions[file_id];
          delete state.requestIds[action.meta.requestId];
        }
      )
      .addMatcher(file.endpoints.deleteFile.matchPending, (state, action) => {
        const fileAction = {
          fileId: action.meta.arg.originalArgs.file_path,
          requestId: action.meta.requestId,
          status: 'delete',
        };
        state.fileActions[action.meta.arg.originalArgs.file_path] = fileAction;
        state.requestIds[action.meta.requestId] = fileAction;
        state.file[action.meta.arg.originalArgs.file_path].loading = true;
      })
      .addMatcher(file.endpoints.deleteFile.matchFulfilled, (state, action) => {
        delete state.file[action.meta.arg.originalArgs.file_path];
        delete state.fileActions[action.meta.arg.originalArgs.file_path];
        delete state.requestIds[action.meta.requestId];
      })
      .addMatcher(file.endpoints.deleteFolder.matchPending, (state, action) => {
        const fileAction = {
          fileId: action.meta.arg.originalArgs.folder_path,
          requestId: action.meta.requestId,
          status: 'delete',
        };
        state.fileActions[action.meta.arg.originalArgs.folder_path] =
          fileAction;
        state.requestIds[action.meta.requestId] = fileAction;
        state.file[action.meta.arg.originalArgs.folder_path].loading = true;
      })
      .addMatcher(
        file.endpoints.deleteFolder.matchFulfilled,
        (state, action) => {
          delete state.file[action.meta.arg.originalArgs.folder_path];
          delete state.fileActions[action.meta.arg.originalArgs.folder_path];
          delete state.requestIds[action.meta.requestId];
        }
      )
      .addMatcher(file.endpoints.copyFile.matchPending, (state, action) => {
        if (action.meta.arg.originalArgs.src) {
          const fileAction = {
            fileId: action.meta.arg.originalArgs.src,
            requestId: action.meta.requestId,
            status: 'copy',
          };
          state.fileActions[action.meta.arg.originalArgs.src] = fileAction;
          state.requestIds[action.meta.requestId] = fileAction;
          state.file[action.meta.arg.originalArgs.src].loading = true;
        } else if (action.meta.arg.originalArgs.srcDir) {
          const fileAction = {
            fileId: action.meta.arg.originalArgs.srcDir,
            requestId: action.meta.requestId,
            status: 'copy',
          };
          state.fileActions[action.meta.arg.originalArgs.srcDir] = fileAction;
          state.requestIds[action.meta.requestId] = fileAction;
          state.file[action.meta.arg.originalArgs.srcDir].loading = true;
        }
      })
      .addMatcher(file.endpoints.copyFile.matchFulfilled, (state, action) => {
        if (
          action.meta.arg.originalArgs.src &&
          action.meta.arg.originalArgs.dst &&
          action.meta.arg.originalArgs.cpy === 'yes'
        ) {
          // Copied a file
          state.file[state.requestIds[action.meta.requestId].fileId].loading =
            false;
        } else if (
          action.meta.arg.originalArgs.srcDir &&
          action.meta.arg.originalArgs.dstDir &&
          action.meta.arg.originalArgs.cpyDir === 'yes'
        ) {
          // Copied a directory
          state.file[state.requestIds[action.meta.requestId].fileId].loading =
            false;
        } else if (
          action.meta.arg.originalArgs.srcDir &&
          action.meta.arg.originalArgs.dstDir
        ) {
          // we moved or renamed a directory
          // state.file[action.meta.arg.originalArgs.dstDir] = {
          //   ...state.file[state.requestIds[action.meta.requestId].fileId],
          //   name: action.meta.arg.originalArgs.dstDir.replace('.hidden', '').split('/').slice(-1)[0],
          //   id: action.meta.arg.originalArgs.dstDir,
          //   loading: false,
          // }
          // delete state.file[state.requestIds[action.meta.requestId].fileId]
          // state.file[state.requestIds[action.meta.requestId].fileId].name = action.meta.arg.originalArgs.dstDir.replace('.hidden', '').split('/').slice(-1)[0]
        } else if (
          action.meta.arg.originalArgs.src &&
          action.meta.arg.originalArgs.dst
        ) {
          // we renamed a file
          // delete state.file[state.requestIds[action.meta.requestId].fileId]
        } else if (
          action.meta.arg.originalArgs.src &&
          action.meta.arg.originalArgs.dstDir
        ) {
          // we moved a file to a different directory
          // delete state.file[state.requestIds[action.meta.requestId].fileId]
        }
        delete state.fileActions[
          state.requestIds[action.meta.requestId].fileId
        ];
        delete state.requestIds[action.meta.requestId];
      }),
});
export const selectFileBrowserState = (state: any) => state.fileBrowser;
export const selectFiles = createSelector(
  [selectFileBrowserState],
  state => state?.file
);
export const selectFile = createSelector(
  [selectFiles, (_: any, file: string) => file],
  (files, file) => files[file]
);

export function* fileSaga() {
  yield all([
    takeEvery(file.endpoints.createFile.matchFulfilled, watchFileUpload),
  ]);
  yield all([
    takeEvery(
      file.endpoints.getFile.matchFulfilled,
      watchFileDownloadSignedUrl
    ),
  ]);
}

export function* watchFileUpload(action: {
  type: string;
  payload: FileUploadPayload;
  meta: any;
}) {
  try {
    const { payload, meta } = action;
    // const data = new FormData();
    // for (var key of Object.keys(payload.fields)) {
    //   data.append(key, (payload as any).fields[key as string] as any)
    // }
    const file = meta?.arg?.originalArgs?.file; //this is where the original args are.
    if (file) {
      // data.append('file', meta?.arg?.originalArgs?.file);
      yield fetch(payload.url, {
        method: 'PUT',
        body: file,
      });
    } else {
      toast.error(`Unable to upload file; Uploading folders is not supported`);
      rollbar.error(
        `Unable to upload file; Uploading folders is not supported`,
        action
      );
    }
    yield put(
      fileBrowserSlice.actions.clearFileAction({ requestId: meta.requestId })
    );
  } catch (error: any) {
    toast.error(
      `Unable to upload file: ${action?.meta?.arg?.originalArgs?.file?.name}; Uploading folders is not supported`
    );
    rollbar.error(`Unable to upload file`, action);
  }
}

///THIS IS FOR PRESIGNED
// export function* watchFileUpload(action: { type: string, payload: FileUploadPayload, meta: any }) {
//   try {
//     const { payload, meta } = action
//     const data = new FormData();
//     for (var key of Object.keys(payload.fields)) {
//       data.append(key, (payload as any).fields[key as string] as any)
//     }
//     const file = meta?.arg?.originalArgs?.file //this is where the original args are.
//     if (file) {
//       data.append('file', meta?.arg?.originalArgs?.file);
//       yield fetch(payload.url, {
//         method: 'POST',
//         body: data,
//       })
//     } else {
//       toast.error(`Unable to upload file`);
//       rollbar.error(`Unable to upload file`, action)
//     }
//     yield put(fileBrowserSlice.actions.clearFileAction({ requestId: meta.requestId }))
//   } catch (error: any) {
//     toast.error(`Unable to upload file: ${action?.meta?.arg?.originalArgs?.file?.name}`);
//     rollbar.error(`Unable to upload file`, action)
//   }
// }

export function* watchFileDownloadSignedUrl(action: {
  type: string;
  payload: {
    url: string;
  };
  meta: any;
}) {
  try {
    const file_name = action.meta.arg.originalArgs.file_path
      .split('/')
      .slice(-1)[0];
    if (action?.payload?.url && action?.payload?.url !== '') {
      yield call(fetchDownloadFile, action.payload.url, file_name);
      yield put(
        fileBrowserSlice.actions.clearFileAction({
          fileId: action.meta.arg.originalArgs.file_path,
        })
      );
    }
  } catch (error: any) {
    const file_name = action.meta.arg.originalArgs.file_path
      .split('/')
      .slice(-1)[0];
    toast.error(`Unable to download file: ${file_name}`);
    rollbar.error(`Unable to download file: ${file_name}`);
  }
}

//@ts-ignore
function* fetchDownloadFile(url: string, file_name: string) {
  //@ts-ignore
  const response = yield fetch(url, {
    method: 'GET',
  });
  //@ts-ignore
  const myBlob = yield response.blob();
  yield fileDownload(myBlob, file_name);
}
